import * as React from 'react';

import { Spinner } from '../../atoms/Spinner';
import { Button } from '../../shadcn/button';
import { AlertBanner } from '../AlertBanner';
import { Dialog, DialogDescription, DialogTitle } from '../Dialog';
import { DialogContent, type DialogContentSize } from '../DialogContent';

export interface ConfirmDialogProps {
  /** Controlled open state. */
  open: boolean;
  /** Open-state change handler. Cancelling (button, backdrop, Escape) calls this with `false`. */
  onOpenChange: (open: boolean) => void;
  /** Accessible title, rendered as `DialogTitle`. */
  title: React.ReactNode;
  /** Optional supporting copy, rendered as `DialogDescription` (wires `aria-describedby`). */
  description?: React.ReactNode;
  /** Label for the confirm (submit) button. */
  confirmLabel: string;
  /** Label for the cancel button. */
  cancelLabel: string;
  /**
   * Confirm handler. The parent owns the mutation and is responsible for
   * passing `pending` / `error` and closing the dialog on success.
   */
  onConfirm: () => void;
  /** Style the confirm button as destructive. @default false */
  destructive?: boolean;
  /** When true, the confirm button shows a spinner and both buttons disable. @default false */
  pending?: boolean;
  /** Last mutation error; when set, renders inline as a critical `AlertBanner`. */
  error?: React.ReactNode;
  /** Disable confirm without showing a spinner (e.g. a required field is empty). @default false */
  confirmDisabled?: boolean;
  /** Optional leading icon shown beside the title (e.g. a warning glyph). */
  icon?: React.ReactNode;
  /** Width preset forwarded to `DialogContent`. @default 'sm' */
  size?: DialogContentSize;
  /** Extra body content rendered between the description and the buttons (form fields, etc.). */
  children?: React.ReactNode;
  className?: string;
}

/**
 * ConfirmDialog is a controlled confirm/cancel modal built on `DialogContent`.
 *
 * The parent owns the action: it passes `pending` while the mutation runs and
 * `error` when it fails, and closes the dialog on success. Content is wrapped
 * in a `<form>` so Enter confirms (the confirm button is the submit button);
 * pass form fields via `children` and gate submission with `confirmDisabled`.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  destructive = false,
  pending = false,
  error,
  confirmDisabled = false,
  icon,
  size = 'sm',
  children,
  className,
}: ConfirmDialogProps) {
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (pending || confirmDisabled) {
      return;
    }
    onConfirm();
  };

  const header = (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <DialogTitle className="text-brand-brown-dark text-lg font-semibold">{title}</DialogTitle>
      {description ? (
        <DialogDescription className="text-text-tertiary text-sm">{description}</DialogDescription>
      ) : null}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size={size} className={className}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {icon ? (
            <div className="flex items-start gap-3">
              {icon}
              {header}
            </div>
          ) : (
            header
          )}

          {children}

          {error ? <AlertBanner variant="critical">{error}</AlertBanner> : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pending}
              onClick={() => {
                onOpenChange(false);
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              type="submit"
              variant="primary"
              tone={destructive ? 'critical' : 'neutral'}
              size="sm"
              loading={pending}
              // Combined so an explicit `false` here can't defeat the loading-disable.
              disabled={confirmDisabled || pending}
            >
              {/* Decorative while pending — the button keeps its visible label
                  and `aria-busy`, so the spinner must not add "Loading" to the
                  button's accessible name. */}
              {pending ? <Spinner size="sm" className="text-current" aria-hidden /> : null}
              {confirmLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
